import audiobufferToWav from 'audiobuffer-to-wav';

let sampleRate = 48000;

async function split(buffer, time) {
	const leftPart = await cut(buffer, 0, time);
	const rightPart = await cut(buffer, time, buffer.duration);

	return [leftPart, rightPart];
}

function initContext(duration, numberOfChannels) {
	if (__DEBUG__)
		console.debug('Audio:init', { duration, numberOfChannels });
	return new OfflineAudioContext({
		numberOfChannels: numberOfChannels || 2,
		length: sampleRate * duration,
		sampleRate
	});
}

function addClicksReduction(source, audioContext, startPos, playDuration) {
	let delay = 0.0025;
	const clicksNode = audioContext.createGain();
	clicksNode.gain.value = 1;

	let playStart = Math.max(0, audioContext.currentTime + startPos);
	let clicksTime = [
		playStart,
		playStart + delay,
		Math.max(0, playStart + playDuration - delay),
		playStart + playDuration
	];
	clicksNode.gain.setValueAtTime(0.0001, clicksTime[0], 0);
	clicksNode.gain.exponentialRampToValueAtTime(1, clicksTime[1]);
	clicksNode.gain.setValueAtTime(1, clicksTime[2], 0);
	clicksNode.gain.exponentialRampToValueAtTime(0.0001, clicksTime[3]);

	source.connect(clicksNode);
	clicksNode.connect(audioContext.destination);

	return clicksNode;
}

async function cut(buffer, start, end) {
	if (__DEBUG__)
		console.debug('Audio:cut', { start, end });
	const offlineAudioCtx = initContext(end - start, buffer.numberOfChannels);
	const source = offlineAudioCtx.createBufferSource();
	source.buffer = buffer;
	source.connect(offlineAudioCtx.destination);
	source.start(offlineAudioCtx.currentTime, start, end - start);

	return offlineAudioCtx.startRendering();
}

async function processBuffer(buffer, processingCallback, start, end) {
	const offlineAudioCtx = initContext(end ? end - (start || 0) : buffer.duration, buffer.numberOfChannels);
	const source = offlineAudioCtx.createBufferSource();
	source.buffer = buffer;

	let output = processingCallback && await processingCallback(source, offlineAudioCtx);

	if (!output) {
		source.connect(offlineAudioCtx.destination);
	}

	source.start(0, start || 0, end || buffer.duration);

	return offlineAudioCtx.startRendering();
}

async function merge(buffers, offsets) {4
	offsets = offsets || [];
	const duration = buffers.reduce((dur, buf) => dur + buf.duration, 0);
	const offlineAudioCtx = initContext(duration, buffers[0]?.numberOfChannels);

	if (__DEBUG__)
		console.debug('Merge', { buffers, offsets });

	let position = 0;
	buffers.map((buffer, i) => {
		let offset = offsets[i] || 0;
		const source = offlineAudioCtx.createBufferSource();
		source.buffer = buffer;

		let start = Math.max(0, position - offset);

		if (__DEBUG__)
			console.debug('Merge buffer processing', { position, offset, start: position - offset });

		addClicksReduction(source, offlineAudioCtx, start, buffer.duration);
		source.start(offlineAudioCtx.currentTime + start);
		position += buffer.duration - offset;
	});

	return offlineAudioCtx.startRendering();
}

function setup(opts) {
	if (opts?.sampleRate)
		sampleRate = opts?.sampleRate;
}

function bufferToWave(audioBuffer) {
	return new Blob([new DataView(audiobufferToWav(audioBuffer))], {
		type: 'audio/wav'
	});
}

function getPeakValue(audioBuffer) {
	let array = audioBuffer.getChannelData(0);
	var gap = audioBuffer.duration * 200;
	var eachBlock = Math.floor(array.length / gap);
	
	let peakPower = 0;
	for(var i = 0; i <= gap; i++){
		var audioBuffKey = Math.floor(eachBlock * i);
		const power = array[audioBuffKey] ** 2 || 0;
		peakPower = Math.max(power, peakPower);
	}
	const peakInstantaneousPowerDecibels = 10 * Math.log10(peakPower);

	return peakInstantaneousPowerDecibels;
}

function fade(buffer, start, end, valStart, valEnd, type) {
	const offlineAudioCtx = initContext(buffer.duration, buffer.numberOfChannels);
	const source = offlineAudioCtx.createBufferSource();
	source.buffer = buffer;

	var invokeFade = (node) => {
		node.gain.setValueAtTime(valStart, start);
		switch (type) {
			case 'linear':
				node.gain.linearRampToValueAtTime(valEnd, end);
				break;
			case 'exponential':
				var curve = createLogarithmicBuffer(1, offlineAudioCtx.samplerate);
				node.gain.setValueCurveAtTime(valEnd > valStart ? curve : curve.reverse(), start, end - start);
				break;
		}
		if (buffer.duration > valEnd) {
			node.gain.setValueAtTime(1, end + 0.001);
		}
	}

	if (__DEBUG__)
		console.debug('Fade', { start, end, valStart, valEnd, type });	

	let fadeNode = null;
	fadeNode = offlineAudioCtx.createGain();

	invokeFade(fadeNode);

	source.connect(fadeNode);
	fadeNode.connect(offlineAudioCtx.destination);
	source.start(offlineAudioCtx.currentTime, 0, buffer.duration);

	return offlineAudioCtx.startRendering();
}

function createLogarithmicBuffer(direction, base, length) {
	base = base || 10;
	length = length || 48000;

	var curve = new Float32Array(length),
		percent = 0,
		index,
		i;

	for (i = 0; i < length; i++) {
		index = direction > 0 ? i : length - 1 - i;

		percent = i / length;
		curve[index] = Math.log(1 + base*percent) / Math.log(1 + base);
	}

	return curve;
}

export default {
	cut,
	split,
	merge,
	setup,
	bufferToWave,
	getPeakValue,
	processBuffer,
	createLogarithmicBuffer,
	fade
};

export {
	cut,
	split,
	merge,
	setup,
	bufferToWave,
	getPeakValue,
	processBuffer,
	createLogarithmicBuffer,
	fade
};
